import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from "react-query";
import Loader from '../../atoms/Loader/LoaderAtom';
import { useLoaderDispatch, useLoaderState } from '../../context/LoaderContext';
import { startLoading, stopLoading } from "../../context/actions/loaderActions";
import { useLocation, useParams } from 'react-router-dom';
import useApiService from '../../utilities/useApiService';
import { useStyles } from './LabelPageStyles';
import { CallToActionButton } from '../../atoms/callToActionButton/CallToActionButton';
import { mediumButton } from '../../utilities/style';
import { renderPdfToImage } from '../../utilities/renderPdfToImage';
import { useReactToPrint } from 'react-to-print';

export interface LabelPageProps { }

type ShipmentLabelResponse = {
    shipmentLabel: string;
    labelContentType: string;
} | string;

type LabelPageLocationState = {
    trackingNumber?: string;
};

const toLabelDataUrl = (base64: string, contentType: string) =>
    `data:${contentType};base64,${base64}`;

const LabelPage: React.FC<LabelPageProps> = () => {
    const { getMethod } = useApiService();
    const params: any = useParams();
    const location = useLocation<LabelPageLocationState>();
    const classes = useStyles();
    const { pharmacyId, requestId, shipmentId } = params;
    const loaderDispatch = useLoaderDispatch();
    const { loading } = useLoaderState();
    const labelRef = useRef<HTMLDivElement | null>(null);
    const trackingNumber = location.state?.trackingNumber || shipmentId;
    const [displaySrc, setDisplaySrc] = useState('');
    const [preparingLabel, setPreparingLabel] = useState(false);
    const [labelError, setLabelError] = useState<string | null>(null);

    const fetchShippingLabel = async () => {
        startLoading(loaderDispatch);
        const response = await getMethod(
            `/pharmacies/${pharmacyId}/returnrequests/${requestId}/shipments/${shipmentId}/label`
        );
        stopLoading(loaderDispatch);
        return response.data as ShipmentLabelResponse;
    };

    const {
        data: shippingLabel,
        isFetched,
        isError: labelFetchError,
    } = useQuery(
        ['shippingLabel', pharmacyId, requestId, shipmentId],
        fetchShippingLabel
    );

    const labelBase64 =
      typeof shippingLabel === 'string'
        ? shippingLabel
        : shippingLabel?.shipmentLabel;
    const contentType =
      typeof shippingLabel === 'string'
        ? 'image/gif'
        : shippingLabel?.labelContentType || 'image/gif';

    useEffect(() => {
        if (!isFetched) {
            return;
        }

        setLabelError(null);

        if (!labelBase64) {
            setDisplaySrc('');
            setLabelError('No shipping label is available for this shipment.');
            setPreparingLabel(false);
            return;
        }

        if (contentType === 'application/pdf') {
            let cancelled = false;
            setPreparingLabel(true);
            renderPdfToImage(labelBase64)
                .then((imageSrc) => {
                    if (!cancelled) {
                        setDisplaySrc(imageSrc);
                        setLabelError(null);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setDisplaySrc('');
                        setLabelError('Failed to render this PDF label. Please try again.');
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setPreparingLabel(false);
                    }
                });

            return () => {
                cancelled = true;
            };
        }

        setDisplaySrc(toLabelDataUrl(labelBase64, contentType));
        setPreparingLabel(false);
        return undefined;
    }, [labelBase64, contentType, isFetched]);

    const handlePrint = useReactToPrint({
        content: () => labelRef.current,
        documentTitle: `shipment-${trackingNumber}`,
    });

    if (loading || preparingLabel || !isFetched) {
        return <Loader />;
    }

    if (labelFetchError || labelError || !displaySrc) {
        return (
            <div className={classes.container}>
                <p>
                    {labelError ||
                        (labelFetchError
                            ? 'Failed to load this shipping label. Please try again.'
                            : 'No shipping label is available for this shipment.')}
                </p>
            </div>
        );
    }

    return (
        <div>
            <div className={classes.container} ref={labelRef}>
                {contentType === 'image/gif' || contentType === 'application/pdf' ? (
                  <div className={classes.rotate}>
                        <img
                            src={displaySrc}
                            alt={`UPS Label of shipment ${trackingNumber}`}
                            className={classes.label}
                        />
                 </div>
                ) : (
                <img
                        src={displaySrc}
                        alt={`Shipment Label of shipment ${trackingNumber}`}
                        className={classes.label}
                    />
                )}
            </div>
            <CallToActionButton
                height={'80px'}
                text='Print Label'
                width={mediumButton.width}
                onClick={handlePrint}
            />
        </div>
    );
};

export default LabelPage;
